// message-store.js — 消息状态管理
const listeners = new Set();
let state = { messages: [] };

export function normalizeAssistantText(value) {
  if (value == null) return '';
  const text = typeof value === 'string' ? value : String(value);
  return /^(?:null|undefined)$/i.test(text.trim()) ? '' : text;
}

export function getMessageState() { return state; }

export function setMessages(messages) {
  state = {
    messages: Array.isArray(messages) ? messages.map(normalizeServerMessage) : [],
  };
  emit();
}

function normalizeServerMessage(m) {
  const isAssistant = String(m.role || '').toUpperCase() === 'ASSISTANT';
  return {
    id: m.id,
    role: m.role,
    content: isAssistant ? normalizeAssistantText(m.content) : (m.content || ''),
    contentType: m.contentType || "TEXT",
    latencyMs: m.latencyMs,
    model: m.model,
    provider: m.provider,
    finishReason: m.finishReason,
    parentId: m.parentId,
    attachments: m.attachments || null,
    createdAt: m.createdAt,
    agentState: m.agentState,
    agentTimeline: Array.isArray(m.agentTimeline) ? m.agentTimeline : [],
    agentSkills: Array.isArray(m.agentSkills) ? m.agentSkills : [],
    agentMemoryLoad: Array.isArray(m.agentMemoryLoad) ? m.agentMemoryLoad : [],
    agentQuality: m.agentQuality || null,
    agentContext: m.agentContext || null,
    agentUsage: m.agentUsage || null,
    agentContextProfile: m.agentContextProfile || null,
    runId: m.runId || null,
    typing: Boolean(m.typing),
  };
}

/**
 * 用服务端快照对账，但不允许“较旧的空快照”覆盖已收到的 SSE 正文/执行链。
 * 这是多窗口 session_ready 拉取与实时事件并发时的核心防抖边界。
 */
export function reconcileServerMessages(serverMessages) {
  if (!Array.isArray(serverMessages)) return 0;
  const currentById = new Map(state.messages.map((message) => [String(message.id), message]));
  const serverIds = new Set();
  const reconciled = serverMessages.map((raw) => {
    const server = normalizeServerMessage(raw);
    const key = String(server.id);
    serverIds.add(key);
    const live = currentById.get(key);
    if (!live) return server;
    const regenerationReset = Boolean(live.finishReason && !server.finishReason);
    return {
      ...live,
      ...server,
      content: server.content || (regenerationReset ? "" : live.content || ""),
      agentTimeline: server.agentTimeline.length ? server.agentTimeline : (live.agentTimeline || []),
      agentSkills: server.agentSkills.length ? server.agentSkills : (live.agentSkills || []),
      agentMemoryLoad: server.agentMemoryLoad.length ? server.agentMemoryLoad : (live.agentMemoryLoad || []),
      agentQuality: server.agentQuality || live.agentQuality || null,
      agentUsage: server.agentUsage || live.agentUsage || null,
      agentContextProfile: server.agentContextProfile || live.agentContextProfile || null,
      agentState: server.agentState || live.agentState,
      typing: server.finishReason ? false : Boolean(live.typing),
    };
  });
  const optimistic = state.messages.filter((message) => !serverIds.has(String(message.id)) && !/^\d+$/.test(String(message.id)));
  state = { messages: [...reconciled, ...optimistic] };
  emit();
  return reconciled.length;
}

export function appendUserMessage(content, attachments = null) {
  const msg = {
    id: `tmp-${Date.now()}-${Math.random()}`,
    role: "USER",
    content,
    contentType: "TEXT",
    attachments: attachments || null,
    createdAt: new Date().toISOString(),
  };
  state = { messages: [...state.messages, msg] };
  emit();
  return msg.id;
}

export function hasMessage(messageId) {
  return state.messages.some((m) => String(m.id) === String(messageId));
}

// T6: 增量合并后端拉回的消息 — 跳过已存在的,只追加新的,emit 一次
// 接收的 message 是后端 DTO 字段(snake_case),需转成前端结构
export function mergeServerMessages(serverMessages) {
  if (!Array.isArray(serverMessages) || serverMessages.length === 0) return 0;
  let added = 0;
  const newMsgs = [];
  for (const m of serverMessages) {
    if (m.id == null) continue;
    if (state.messages.some(s => String(s.id) === String(m.id))) continue;
    newMsgs.push(normalizeServerMessage(m));
    added++;
  }
  if (added > 0) {
    state = { messages: [...state.messages, ...newMsgs] };
    emit();
  }
  return added;
}

export function upsertAssistantMessage({ messageId, model, provider, agentState, agentContext }) {
  const existing = state.messages.find(m => String(m.id) === String(messageId));
  if (existing) return; // already added
  const msg = {
    id: messageId,
    role: "ASSISTANT",
    content: "",
    contentType: "TEXT",
    model,
    provider,
    agentState,
    agentContext,
    createdAt: new Date().toISOString(),
    typing: true,
  };
  state = { messages: [...state.messages, msg] };
  emit();
}

/** 将运行中的占位消息提升为持久化 assistant 消息，同时保留线性执行时间线。 */
export function promoteAgentMessage({ fromId, messageId, model, provider, agentState, agentContext }) {
  const source = state.messages.find((message) => String(message.id) === String(fromId));
  if (!source) return upsertAssistantMessage({ messageId, model, provider, agentState, agentContext });
  const target = state.messages.find((message) => String(message.id) === String(messageId));
  if (target && target !== source) {
    state = {
      messages: state.messages
        .filter((message) => String(message.id) !== String(fromId))
        .map((message) => String(message.id) === String(messageId)
          ? {
              ...message,
              content: source.content || message.content,
              agentTimeline: source.agentTimeline?.length ? source.agentTimeline : message.agentTimeline,
              agentSkills: source.agentSkills?.length ? source.agentSkills : message.agentSkills,
              agentMemoryLoad: source.agentMemoryLoad?.length ? source.agentMemoryLoad : message.agentMemoryLoad,
              agentQuality: source.agentQuality || message.agentQuality || null,
              model: model || source.model || message.model,
              provider: provider || source.provider || message.provider,
              agentState: agentState || source.agentState || message.agentState,
              agentContext: agentContext || source.agentContext || message.agentContext,
              typing: true,
            }
          : message),
    };
    emit();
    return;
  }
  state = {
    messages: state.messages.map((message) => String(message.id) === String(fromId)
      ? { ...message, id: messageId, model: model || message.model, provider: provider || message.provider, agentState: agentState || message.agentState, agentContext: agentContext || message.agentContext, typing: true }
      : message),
  };
  emit();
}

/** 将乐观插入的用户消息替换为服务端持久化 id，避免多窗口同步时重复展示。 */
export function promoteUserMessage({ fromId, messageId }) {
  if (!fromId || messageId == null) return;
  const source = state.messages.find((message) => String(message.id) === String(fromId));
  if (!source) return;
  if (state.messages.some((message) => String(message.id) === String(messageId))) {
    state = { messages: state.messages.filter((message) => String(message.id) !== String(fromId)) };
    emit();
    return;
  }
  state = {
    messages: state.messages.map((message) => String(message.id) === String(fromId)
      ? { ...message, id: messageId }
      : message),
  };
  emit();
}

export function appendAgentTimelineStep(id, step) {
  if (!step?.kind || !step?.title) return;
  state = {
    messages: state.messages.map((message) => {
      if (String(message.id) !== String(id)) return message;
      const timeline = Array.isArray(message.agentTimeline) ? [...message.agentTimeline] : [];
      const key = `${step.round ?? ""}:${step.kind}:${step.title}`;
      const existingIndex = timeline.findIndex((item) => item.key === key);
      const item = { key, round: step.round ?? null, kind: step.kind, title: step.title, summary: step.summary || "" };
      if (existingIndex >= 0) timeline[existingIndex] = item;
      else timeline.push(item);
      return { ...message, agentTimeline: timeline, typing: true };
    }),
  };
  emit();
}

export function appendAssistantDelta(messageId, delta) {
  const text = normalizeAssistantText(delta);
  if (!text) return;
  state = {
    messages: state.messages.map(m =>
      String(m.id) === String(messageId) ? { ...m, typing: false, content: `${m.content || ''}${text}` } : m
    ),
  };
  emit();
}

let _rafPending = false;
export function replaceAssistantContent(messageId, fullText) {
  const text = normalizeAssistantText(fullText);
  state = {
    messages: state.messages.map(m =>
      String(m.id) === String(messageId) ? { ...m, typing: false, content: text } : m
    ),
  };
  // Throttled emit via rAF — avoids flicker from full innerHTML rebuild
  if (!_rafPending) {
    _rafPending = true;
    requestAnimationFrame(() => { _rafPending = false; emit(); });
  }
}

export function finalizeAssistant(messageId, { latencyMs, finishReason }) {
  state = {
    messages: state.messages.map(m =>
      String(m.id) === String(messageId) ? { ...m, typing: false, latencyMs, finishReason } : m
    ),
  };
  emit();
}

export function appendToolCall({ id, name, args }) {
  const msg = {
    id: `tool-call-${id || Date.now()}`,
    role: "TOOL",
    content: `🔧 调用工具: ${name}`,
    contentType: "TOOL_CALL",
    toolData: { id, name, args },
    createdAt: new Date().toISOString(),
  };
  state = { messages: [...state.messages, msg] };
  emit();
}

export function appendToolResult({ id, name, result, status = null, latencyMs = null, errorMessage = null }) {
  // T4: status/latencyMs/errorMessage 用于前端渲染红气泡与耗时
  const isError = status && String(status).toUpperCase() === "ERROR";
  const msg = {
    id: `tool-result-${id || Date.now()}`,
    role: "TOOL",
    content: isError ? `❌ 工具失败: ${name}` : `✅ 工具结果: ${name}`,
    contentType: "TOOL_RESULT",
    toolData: { id, name, result, status, latencyMs, errorMessage },
    createdAt: new Date().toISOString(),
  };
  state = { messages: [...state.messages, msg] };
  emit();
}

export function appendTypingIndicator(id) {
  const existing = state.messages.find(m => String(m.id) === String(id));
  if (existing) return;
  const msg = {
    id,
    role: "ASSISTANT",
    content: "",
    contentType: "TEXT",
    createdAt: new Date().toISOString(),
    typing: true,
  };
  state = { messages: [...state.messages, msg] };
  emit();
}

export function setAgentStatus(id, { state: agentState, context }) {
  state = {
    messages: state.messages.map(m =>
      String(m.id) === String(id) || (String(m.id) === String(context?.assistant_message_id))
        ? { ...m, agentState, agentContext: context, typing: true }
        : m
    ),
  };
  emit();
}

export function setAgentMemoryLoad(id, summaries) {
  state = {
    messages: state.messages.map(m =>
      String(m.id) === String(id) ? { ...m, agentMemoryLoad: summaries, typing: true } : m
    ),
  };
  emit();
}

export function setAgentQuality(id, quality) {
  state = {
    messages: state.messages.map(m =>
      String(m.id) === String(id) ? { ...m, agentQuality: quality || null, typing: true } : m
    ),
  };
  emit();
}

export function appendAgentSkill(id, { skillId, name, version = null, purpose = "", tools = [], unavailableTools = [], versionSource = "stable" }) {
  state = {
    messages: state.messages.map(m => {
      if (String(m.id) !== String(id)) return m;
      const skills = Array.isArray(m.agentSkills) ? [...m.agentSkills] : [];
      const index = skills.findIndex((skill) => String(skill.skillId) === String(skillId));
      const snapshot = { skillId, name, version, purpose, tools, unavailableTools, versionSource };
      if (index >= 0) skills[index] = snapshot;
      else skills.push(snapshot);
      return { ...m, agentSkills: skills, typing: true };
    }),
  };
  emit();
}

export function removeMessage(id) {
  const filtered = state.messages.filter(m => String(m.id) !== String(id));
  if (filtered.length !== state.messages.length) {
    state = { messages: filtered };
    emit();
  }
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

function emit() {
  for (const fn of listeners) fn(state);
}

// ── Public store object (must be after all functions) ──
export const messageStore = Object.freeze({
  getMessageState,
  setMessages,
  reconcileServerMessages,
  appendUserMessage,
  hasMessage,
  mergeServerMessages,
  upsertAssistantMessage,
  promoteAgentMessage,
  promoteUserMessage,
  appendAssistantDelta,
  replaceAssistantContent,
  finalizeAssistant,
  appendToolCall,
  appendToolResult,
  appendTypingIndicator,
  removeMessage,
  setAgentStatus,
  setAgentMemoryLoad,
  setAgentQuality,
  appendAgentSkill,
  appendAgentTimelineStep,
  subscribe,
});
